import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { CloseIcon } from "@/assets/icons";
import { getModuleConfig } from "@/components/onboarding/configs/getModuleConfig";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { MESSAGE } from "@/constants/message";
import { useEnterOnboardingMode } from "@/hooks/onboarding/useOnboarding";
import {
  completeFirstLoginOnboarding,
  shouldShowFirstLoginOnboarding
} from "@/utils/onboardingFirstLogin";

import { OnboardingIntroDialog } from "./ProductOverviewPanel/OnboardingIntroDialog";
import { OnboardingVideoCard } from "./ProductOverviewPanel/OnboardingVideoCard";
import { ONBOARDING_VIDEOS } from "./ProductOverviewPanel/videos";

const WORKSPACE_TOUR_MODULE_CODE = "workspace-tour";
const firstLoginVideo = ONBOARDING_VIDEOS[0];
const workspaceTourIntro = getModuleConfig(WORKSPACE_TOUR_MODULE_CODE)?.intro;

export const FirstLoginOnboardingDialog = () => {
  const navigate = useNavigate();
  const enterOnboardingMode = useEnterOnboardingMode();
  const [open, setOpen] = useState(() => shouldShowFirstLoginOnboarding());
  const [showIntro, setShowIntro] = useState(false);

  if (!firstLoginVideo) return null;

  const startWorkspaceTour = async () => {
    try {
      await enterOnboardingMode(WORKSPACE_TOUR_MODULE_CODE);
    } catch (error) {
      console.error(error);
    }
  };

  const handleContinueOnboarding = async () => {
    completeFirstLoginOnboarding();
    setOpen(false);
    navigate("/", { replace: true });

    // Surface the workspace-tour intro before the tour steps begin. Mirrors the
    // Product Overview flow (OnboardingMenu) where the intro precedes the tour.
    // Fall back to entering the tour directly if the module has no intro config.
    if (workspaceTourIntro) {
      setShowIntro(true);
      return;
    }

    await startWorkspaceTour();
  };

  const handleIntroOpenChange = (next: boolean) => {
    if (!next) setShowIntro(false);
  };

  const handleIntroStart = async () => {
    setShowIntro(false);
    await startWorkspaceTour();
  };

  return (
    <>
      <Dialog open={open}>
        <DialogContent
          isHideClose
          onEscapeKeyDown={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="w-[860px] h-[645px] max-h-[calc(100vh-32px)] max-w-[calc(100vw-32px)] gap-0 rounded-2xl border-0 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]"
        >
          <div className="relative flex flex-col gap-4">
            <DialogClose asChild>
              <button
                type="button"
                aria-label={MESSAGE.FIRST_LOGIN_ONBOARDING.CONTINUE_ARIA_LABEL}
                className="absolute right-0 top-0 flex h-6 w-6 items-center justify-center rounded-full text-dim-gray hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2"
                onClick={() => handleContinueOnboarding()}
              >
                <CloseIcon className="h-6 w-6 fill-current" />
              </button>
            </DialogClose>

            <div className="flex flex-col gap-2 pr-9">
              <DialogTitle className="text-xl font-bold text-txtHeading">
                {MESSAGE.FIRST_LOGIN_ONBOARDING.TITLE}
              </DialogTitle>
              <DialogDescription className="text-sm text-dim-gray sr-only">
                {MESSAGE.FIRST_LOGIN_ONBOARDING.DESCRIPTION}
              </DialogDescription>
            </div>

            <div className="flex flex-1">
              <OnboardingVideoCard video={firstLoginVideo} />
            </div>

            <p className="text-sm text-black">
              {MESSAGE.FIRST_LOGIN_ONBOARDING.BODY}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {workspaceTourIntro && (
        <OnboardingIntroDialog
          intro={workspaceTourIntro}
          open={showIntro}
          onOpenChange={handleIntroOpenChange}
          onStart={handleIntroStart}
        />
      )}
    </>
  );
};

export default FirstLoginOnboardingDialog;
